/**
 * Patient Context Indicators Group
 *
 * Self-contained unit that owns the patient context indicator row:
 * data fetching, hover state, popover rendering, edit modals, markdown
 * importer, and analytics. Consumers just supply a `patientId` + `source`
 * — no prop drilling, no duplicated wiring.
 *
 * Used by PatientContextBar (encounter view) and NoteMetadata (note chat
 * view). Adding a new indicator type, edit modal, or analytics event
 * should happen here so both surfaces get it simultaneously.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import {
  AllergyPeekList,
  DiagnosisPeekList,
  mapAllergiesToVM,
  mapDiagnosesToVM,
  UnifiedMedicationPeek,
} from '@features/medications-workspace';
import { useGetEncounterPriorAuthorizationContextQuery } from '@features/patient-coverages/store/api';
import { useEventTracking } from '@services/statsig';
import { useModal } from '@store/hooks';

import { useIndicatorHover } from '../hooks/useIndicatorHover';
import { usePatientContext } from '../hooks/usePatientContext';

import { PatientAllergiesPopover } from './PatientAllergiesPopover';
import { PatientDiagnosesPopover } from './PatientDiagnosesPopover';
import { PatientIndicators } from './PatientIndicators';
import { PatientLastEncounterPopover } from './PatientLastEncounterPopover';
import { PatientMedicationsPopover } from './PatientMedicationsPopover';
import { TREATMENT_PLAN_EDIT_DIALOG_DEFAULTS } from './PatientTreatmentPlanEditModal.constants';
import { PatientTreatmentPlanPopover } from './PatientTreatmentPlanPopover';
import { PriorAuthorizationPopover } from './PriorAuthorizationPopover';
import { UnifiedClinicalEditModal } from './UnifiedClinicalEditModal';

export interface PatientContextIndicatorsGroupProps {
  /** Patient ID to fetch context for. When null, nothing renders. */
  patientId: string | null;
  /** Encounter anchors authorization matching to the visit's date and codes. */
  encounterId?: string | null;
  /** Analytics source identifier (e.g. 'encounter', 'note', 'jotaudit'). */
  source: string;
  /** Called when the user clicks "view note" on the last encounter popover. */
  onViewLastEncounterNote?: (noteId: string) => void;
}

/**
 * Renders the full patient context indicator row with all hover popovers,
 * edit modals, and the treatment plan markdown importer. Owns all related
 * state and analytics.
 */
export const PatientContextIndicatorsGroup: React.FC<PatientContextIndicatorsGroupProps> = ({
  patientId,
  encounterId,
  source,
  onViewLastEncounterNote,
}) => {
  // Fetch patient context data
  const { medications, diagnoses, allergies, lastEncounter, treatmentPlan, loadingState } =
    usePatientContext(patientId);
  const { data: priorAuthorization } = useGetEncounterPriorAuthorizationContextQuery(
    { encounterId: encounterId ?? '' },
    { skip: !encounterId },
  );

  // Event tracking
  const { patientContextBar: contextBarTracking } = useEventTracking();

  // Global modal system
  const { open } = useModal();

  // Modal state (medications, diagnoses, allergies remain local)
  const [medicationsModalOpen, setMedicationsModalOpen] = useState(false);
  const [diagnosesModalOpen, setDiagnosesModalOpen] = useState(false);
  const [allergiesModalOpen, setAllergiesModalOpen] = useState(false);

  // Hover state per indicator
  const medicationsHover = useIndicatorHover();
  const diagnosesHover = useIndicatorHover();
  const allergiesHover = useIndicatorHover();
  const lastEncounterHover = useIndicatorHover();
  const treatmentPlanHover = useIndicatorHover();
  const priorAuthorizationHover = useIndicatorHover();

  // Derived popover-open booleans (used for analytics effects)
  const hasPatient = !!patientId;
  const medicationsPopoverOpen =
    medicationsHover.isHovered && !!medicationsHover.anchorEl && hasPatient;
  const diagnosesPopoverOpen = diagnosesHover.isHovered && !!diagnosesHover.anchorEl && hasPatient;
  const allergiesPopoverOpen = allergiesHover.isHovered && !!allergiesHover.anchorEl && hasPatient;
  const lastEncounterPopoverOpen =
    lastEncounterHover.isHovered && !!lastEncounterHover.anchorEl && hasPatient;
  const treatmentPlanPopoverOpen =
    treatmentPlanHover.isHovered && !!treatmentPlanHover.anchorEl && hasPatient;
  const priorAuthorizationVisible =
    priorAuthorization?.indicator_state === 'progress' ||
    priorAuthorization?.indicator_state === 'attention';
  const priorAuthorizationPopoverOpen =
    priorAuthorizationHover.isHovered &&
    !!priorAuthorizationHover.anchorEl &&
    hasPatient &&
    priorAuthorizationVisible;

  // Track popover views (fire once per open)
  const prevMedsOpen = useRef(false);
  const prevDiagsOpen = useRef(false);
  const prevAllergiesOpen = useRef(false);
  const prevEncounterOpen = useRef(false);
  const prevTreatmentPlanOpen = useRef(false);
  const prevPriorAuthorizationOpen = useRef(false);

  useEffect(() => {
    if (medicationsPopoverOpen && !prevMedsOpen.current && patientId) {
      contextBarTracking.popoverViewed({
        patient_id: patientId,
        indicator_type: 'medications',
        item_count: medications.length,
        source,
      });
    }
    prevMedsOpen.current = medicationsPopoverOpen;
  }, [medicationsPopoverOpen, patientId, medications.length, source, contextBarTracking]);

  useEffect(() => {
    if (diagnosesPopoverOpen && !prevDiagsOpen.current && patientId) {
      contextBarTracking.popoverViewed({
        patient_id: patientId,
        indicator_type: 'diagnoses',
        item_count: diagnoses.length,
        source,
      });
    }
    prevDiagsOpen.current = diagnosesPopoverOpen;
  }, [diagnosesPopoverOpen, patientId, diagnoses.length, source, contextBarTracking]);

  useEffect(() => {
    if (allergiesPopoverOpen && !prevAllergiesOpen.current && patientId) {
      contextBarTracking.popoverViewed({
        patient_id: patientId,
        indicator_type: 'allergies',
        item_count: allergies.length,
        source,
      });
    }
    prevAllergiesOpen.current = allergiesPopoverOpen;
  }, [allergiesPopoverOpen, patientId, allergies.length, source, contextBarTracking]);

  useEffect(() => {
    if (lastEncounterPopoverOpen && !prevEncounterOpen.current && patientId) {
      contextBarTracking.popoverViewed({
        patient_id: patientId,
        indicator_type: 'last_encounter',
        source,
      });
    }
    prevEncounterOpen.current = lastEncounterPopoverOpen;
  }, [lastEncounterPopoverOpen, patientId, source, contextBarTracking]);

  useEffect(() => {
    if (treatmentPlanPopoverOpen && !prevTreatmentPlanOpen.current && patientId) {
      contextBarTracking.popoverViewed({
        patient_id: patientId,
        indicator_type: 'treatment_plan',
        source,
      });
    }
    prevTreatmentPlanOpen.current = treatmentPlanPopoverOpen;
  }, [treatmentPlanPopoverOpen, patientId, source, contextBarTracking]);

  useEffect(() => {
    if (priorAuthorizationPopoverOpen && !prevPriorAuthorizationOpen.current && patientId) {
      contextBarTracking.popoverViewed({
        patient_id: patientId,
        indicator_type: 'prior_authorization',
        source,
      });
    }
    prevPriorAuthorizationOpen.current = priorAuthorizationPopoverOpen;
  }, [priorAuthorizationPopoverOpen, patientId, source, contextBarTracking]);

  // Indicator click handlers (open modal + fire editModalOpened)
  const handleMedicationsClick = useCallback(() => {
    if (!patientId) return;
    setMedicationsModalOpen(true);
    contextBarTracking.editModalOpened({
      patient_id: patientId,
      data_type: 'medications',
      item_count: medications.length,
      source,
    });
  }, [patientId, medications.length, source, contextBarTracking]);

  const handleDiagnosesClick = useCallback(() => {
    if (!patientId) return;
    setDiagnosesModalOpen(true);
    contextBarTracking.editModalOpened({
      patient_id: patientId,
      data_type: 'diagnoses',
      item_count: diagnoses.length,
      source,
    });
  }, [patientId, diagnoses.length, source, contextBarTracking]);

  const handleAllergiesClick = useCallback(() => {
    if (!patientId) return;
    setAllergiesModalOpen(true);
    contextBarTracking.editModalOpened({
      patient_id: patientId,
      data_type: 'allergies',
      item_count: allergies.length,
      source,
    });
  }, [patientId, allergies.length, source, contextBarTracking]);

  const handleTreatmentPlanEdit = useCallback(() => {
    if (!patientId) return;
    // `editModalOpened` is emitted inside PatientTreatmentPlanEditModal so
    // every open path (context bar, server-UI action dispatch, etc.) produces
    // a matching open/close pair.
    open(MODAL_TYPES.TREATMENT_PLAN_EDIT, {
      patientId,
      source,
      ...TREATMENT_PLAN_EDIT_DIALOG_DEFAULTS,
    });
  }, [patientId, source, open]);

  // Edit-from-popover handlers (fire editFromPopoverClicked, then open modal)
  const handleMedicationsEditFromPopover = useCallback(() => {
    if (!patientId) return;
    contextBarTracking.editFromPopoverClicked({
      patient_id: patientId,
      data_type: 'medications',
      source,
    });
    handleMedicationsClick();
  }, [patientId, source, contextBarTracking, handleMedicationsClick]);

  const handleDiagnosesEditFromPopover = useCallback(() => {
    if (!patientId) return;
    contextBarTracking.editFromPopoverClicked({
      patient_id: patientId,
      data_type: 'diagnoses',
      source,
    });
    handleDiagnosesClick();
  }, [patientId, source, contextBarTracking, handleDiagnosesClick]);

  const handleAllergiesEditFromPopover = useCallback(() => {
    if (!patientId) return;
    contextBarTracking.editFromPopoverClicked({
      patient_id: patientId,
      data_type: 'allergies',
      source,
    });
    handleAllergiesClick();
  }, [patientId, source, contextBarTracking, handleAllergiesClick]);

  const handleTreatmentPlanEditFromPopover = useCallback(() => {
    if (!patientId) return;
    contextBarTracking.editFromPopoverClicked({
      patient_id: patientId,
      data_type: 'treatment_plan',
      source,
    });
    handleTreatmentPlanEdit();
  }, [patientId, source, contextBarTracking, handleTreatmentPlanEdit]);

  // Medications fetch their unified history through the peek container;
  // diagnoses/allergies reuse the rows this hook already holds.
  const diagnosisVms = useMemo(() => mapDiagnosesToVM(diagnoses), [diagnoses]);
  const allergyVms = useMemo(() => mapAllergiesToVM(allergies), [allergies]);

  // "View note" analytics wrapper
  const handleViewNote = useCallback(
    (noteId: string) => {
      if (patientId) {
        contextBarTracking.viewNoteClicked({
          patient_id: patientId,
          note_id: noteId,
          source,
        });
      }
      onViewLastEncounterNote?.(noteId);
    },
    [patientId, source, contextBarTracking, onViewLastEncounterNote],
  );

  if (!patientId) {
    return null;
  }

  return (
    <>
      <PatientIndicators
        medicationsHover={medicationsHover}
        diagnosesHover={diagnosesHover}
        allergiesHover={allergiesHover}
        lastEncounterHover={lastEncounterHover}
        treatmentPlanHover={treatmentPlanHover}
        treatmentPlan={treatmentPlan}
        priorAuthorizationHover={priorAuthorizationHover}
        priorAuthorization={priorAuthorizationVisible ? priorAuthorization : null}
        onMedicationsClick={handleMedicationsClick}
        onDiagnosesClick={handleDiagnosesClick}
        onAllergiesClick={handleAllergiesClick}
        onTreatmentPlanClick={handleTreatmentPlanEdit}
      />

      {/* Medications Popover */}
      <PatientMedicationsPopover
        anchorEl={medicationsHover.anchorEl}
        open={medicationsPopoverOpen}
        onClose={() => {
          medicationsHover.handlePopoverLeave();
        }}
        onMouseEnter={medicationsHover.handlePopoverEnter}
        onMouseLeave={medicationsHover.handlePopoverLeave}
        content={
          <UnifiedMedicationPeek patientId={patientId} onEdit={handleMedicationsEditFromPopover} />
        }
      />

      {/* Diagnoses Popover */}
      <PatientDiagnosesPopover
        anchorEl={diagnosesHover.anchorEl}
        open={diagnosesPopoverOpen}
        onClose={() => {
          diagnosesHover.handlePopoverLeave();
        }}
        onMouseEnter={diagnosesHover.handlePopoverEnter}
        onMouseLeave={diagnosesHover.handlePopoverLeave}
        content={
          <DiagnosisPeekList
            diagnoses={diagnosisVms}
            isLoading={loadingState.diagnoses}
            onEdit={handleDiagnosesEditFromPopover}
          />
        }
      />

      {/* Allergies Popover */}
      <PatientAllergiesPopover
        anchorEl={allergiesHover.anchorEl}
        open={allergiesPopoverOpen}
        onClose={() => {
          allergiesHover.handlePopoverLeave();
        }}
        onMouseEnter={allergiesHover.handlePopoverEnter}
        onMouseLeave={allergiesHover.handlePopoverLeave}
        content={
          <AllergyPeekList
            allergies={allergyVms}
            isLoading={loadingState.allergies}
            onEdit={handleAllergiesEditFromPopover}
          />
        }
      />

      {/* Last Encounter Popover */}
      <PatientLastEncounterPopover
        data={lastEncounter}
        anchorEl={lastEncounterHover.anchorEl}
        open={lastEncounterPopoverOpen}
        onClose={() => {
          lastEncounterHover.handlePopoverLeave();
        }}
        onMouseEnter={lastEncounterHover.handlePopoverEnter}
        onMouseLeave={lastEncounterHover.handlePopoverLeave}
        isLoading={loadingState.lastEncounter}
        onViewNote={handleViewNote}
      />

      {/* These dialogs host the same clinical-card containers as the patient
          page. Fine-grained CRUD analytics come from those containers while
          this surface retains its editModalOpened/editModalClosed pair. */}
      <UnifiedClinicalEditModal
        module="medications"
        open={medicationsModalOpen}
        onClose={() => setMedicationsModalOpen(false)}
        patientId={patientId}
        source={source}
      />
      <UnifiedClinicalEditModal
        module="diagnoses"
        open={diagnosesModalOpen}
        onClose={() => setDiagnosesModalOpen(false)}
        patientId={patientId}
        source={source}
      />
      <UnifiedClinicalEditModal
        module="allergies"
        open={allergiesModalOpen}
        onClose={() => setAllergiesModalOpen(false)}
        patientId={patientId}
        source={source}
      />

      {/* Treatment Plan Popover */}
      <PatientTreatmentPlanPopover
        data={treatmentPlan}
        anchorEl={treatmentPlanHover.anchorEl}
        open={treatmentPlanPopoverOpen}
        onClose={() => {
          treatmentPlanHover.handlePopoverLeave();
        }}
        onMouseEnter={treatmentPlanHover.handlePopoverEnter}
        onMouseLeave={treatmentPlanHover.handlePopoverLeave}
        isLoading={loadingState.treatmentPlan}
        onEdit={handleTreatmentPlanEditFromPopover}
      />
      {priorAuthorizationVisible && priorAuthorization ? (
        <PriorAuthorizationPopover
          context={priorAuthorization}
          anchorEl={priorAuthorizationHover.anchorEl}
          open={priorAuthorizationPopoverOpen}
          onClose={priorAuthorizationHover.handlePopoverLeave}
          onMouseEnter={priorAuthorizationHover.handlePopoverEnter}
          onMouseLeave={priorAuthorizationHover.handlePopoverLeave}
        />
      ) : null}
    </>
  );
};
